<template>
  <div class="pathology-report-panel">
    <div class="report-body">
      <div class="report-toolbar">
        <div class="field report-disease-field">
          <label class="label">{{ $t('report-disease-type') }}</label>
          <div class="control">
            <div class="select is-fullwidth">
              <select v-model="selectedTemplateType" data-testid="disease-type-select">
                <option v-for="option in templateOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <button
          class="button report-action-button import-ai-button"
          type="button"
          disabled
          :title="$t('ai-import-placeholder-help')"
          data-testid="ai-import-button"
        >
          <span class="icon is-small report-button-icon">
            <i class="fas fa-sync-alt" aria-hidden="true"></i>
          </span>
          <span>{{ $t('button-sync') }}</span>
        </button>
        <button
          class="button report-action-button export-pdf-button"
          type="button"
          disabled
          :title="$t('pdf-export-placeholder-help')"
          data-testid="export-pdf-button"
        >
          <span class="icon is-small report-button-icon">
            <i class="fas fa-file-export" aria-hidden="true"></i>
          </span>
          <span>{{ $t('button-export') }}</span>
        </button>
      </div>

      <component :is="activeTemplate.component" :report="activeReport" />

      <section class="report-section report-preview-section">
        <div class="report-preview-heading">
          <h3>{{ $t('structured-report-preview-section') }}</h3>
        </div>
        <pre class="report-preview" data-testid="structured-report-preview">{{ generatedReportText }}</pre>
      </section>

      <p v-if="saveMessage" class="help save-placeholder-message" data-testid="save-placeholder-message">
        {{ saveMessage }}
      </p>
    </div>
    <div class="report-footer">
      <button class="button report-footer-button report-save-button" type="button" @click="handleSave">{{ $t('button-save') }}</button>
      <button class="button report-footer-button report-cancel-button" type="button" @click="$emit('close')">{{ $t('button-cancel') }}</button>
    </div>
  </div>
</template>

<script>
import { findReportTemplate, reportTemplates } from './pathology-report-templates';

export default {
  name: 'PathologyReportPanel',
  data() {
    return {
      saveMessage: '',
      selectedTemplateType: 'general',
      reportsByTemplateType: reportTemplates.reduce((reports, template) => {
        reports[template.value] = template.createReport();
        return reports;
      }, {})
    };
  },
  computed: {
    activeTemplate() {
      return findReportTemplate(this.selectedTemplateType);
    },
    activeReport() {
      return this.reportsByTemplateType[this.activeTemplate.value];
    },
    templateOptions() {
      return reportTemplates.map(template => ({
        value: template.value,
        label: this.$t(template.labelKey)
      }));
    },
    selectedTemplateLabel() {
      const option = this.templateOptions.find(template => template.value === this.activeTemplate.value);
      return option ? option.label : this.activeTemplate.value;
    },
    generatedReportText() {
      return this.activeTemplate.buildReportText(this.activeReport, this.$t.bind(this), this.selectedTemplateLabel);
    }
  },
  methods: {
    handleSave() {
      const message = this.$t('report-save-placeholder-message');
      this.saveMessage = message;
      if (this.$buefy && this.$buefy.toast) {
        this.$buefy.toast.open({
          message,
          type: 'is-warning',
          duration: 3000
        });
      }
    }
  }
};
</script>

<style lang="scss">
@import '../../assets/styles/dark-variables';

.pathology-report-panel {
  --report-accent: #5fd7e8;
  --report-border: rgba(190, 214, 238, 0.16);
  --report-border-strong: rgba(95, 215, 232, 0.34);
  --report-text: #f5fbff;
  --report-muted: #bfd0df;
  --report-subtle: #91a3b7;
  --report-radius: 8px;

  width: 100%;
  color: var(--report-text);

  .report-body,
  .shared-proforma-fields,
  .shared-ending-fields,
  .prostate-report-template,
  .bladder-report-template,
  .generic-report-template {
    display: flex;
    flex-direction: column;
    gap: 0.82rem;
  }

  .report-body {
    gap: 0.9rem;
  }

  .report-toolbar {
    display: grid;
    grid-template-columns: minmax(180px, 1fr) 5.8rem 5.8rem;
    gap: 0.55rem;
    align-items: end;
    padding: 0.76rem;
    border: 1px solid var(--report-border);
    border-radius: var(--report-radius);
    background:
      linear-gradient(180deg, rgba(222, 238, 255, 0.072), rgba(222, 238, 255, 0.028)),
      rgba(12, 23, 38, 0.74);
    box-shadow: 0 12px 26px rgba(0, 0, 0, 0.18);
  }

  .report-disease-field,
  .report-toolbar .field {
    margin-bottom: 0;
  }

  .button.report-action-button,
  .button.report-footer-button,
  .add-measurement-button,
  .remove-measurement-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.36rem;
    min-height: 2.25rem;
    padding: 0 0.72rem;
    border: 1px solid rgba(178, 204, 230, 0.2);
    border-radius: 6px;
    background: rgba(222, 238, 255, 0.06);
    color: var(--report-text);
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    transition: background-color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease;
  }

  .report-button-icon {
    width: auto;
    height: auto;
    margin: 0;
    color: currentColor;
    font-size: 0.72rem;
    line-height: 1;
  }

  .button.report-action-button:hover,
  .button.report-footer-button:hover,
  .add-measurement-button:hover,
  .remove-measurement-button:hover {
    border-color: var(--report-border-strong);
    background: rgba(95, 215, 232, 0.12);
    color: var(--report-accent);
    box-shadow: 0 0 0 2px rgba(95, 215, 232, 0.12);
  }

  .button.report-action-button:focus,
  .button.report-action-button:active,
  .button.report-action-button:focus:not(:active),
  .button.report-footer-button:focus,
  .button.report-footer-button:active,
  .button.report-footer-button:focus:not(:active),
  .add-measurement-button:focus,
  .add-measurement-button:active,
  .add-measurement-button:focus:not(:active),
  .remove-measurement-button:focus,
  .remove-measurement-button:active,
  .remove-measurement-button:focus:not(:active) {
    border-color: var(--report-border-strong);
    background: rgba(95, 215, 232, 0.11);
    color: var(--report-accent);
    box-shadow: 0 0 0 2px rgba(95, 215, 232, 0.16);
    outline: none;
  }

  .button.report-action-button[disabled],
  .button.report-action-button[disabled]:hover,
  .button.report-action-button[disabled]:focus,
  .button.report-action-button[disabled]:active,
  .button.report-action-button[disabled]:focus:not(:active) {
    border-color: rgba(178, 204, 230, 0.12);
    background: rgba(222, 238, 255, 0.035);
    color: rgba(210, 222, 234, 0.58);
    box-shadow: none;
    opacity: 1;
  }

  .button.report-save-button {
    border-color: rgba(69, 212, 131, 0.38);
    background: rgba(69, 212, 131, 0.15);
    color: #dffceb;
  }

  .button.report-save-button:hover {
    border-color: rgba(69, 212, 131, 0.62);
    background: rgba(69, 212, 131, 0.22);
    color: #ffffff;
    box-shadow: 0 0 0 2px rgba(69, 212, 131, 0.13);
  }

  .button.report-save-button:focus,
  .button.report-save-button:active,
  .button.report-save-button:focus:not(:active) {
    border-color: rgba(69, 212, 131, 0.62);
    background: rgba(69, 212, 131, 0.2);
    color: #ffffff;
    box-shadow: 0 0 0 2px rgba(69, 212, 131, 0.16);
  }

  .button.report-cancel-button {
    color: var(--report-muted);
  }

  .report-section {
    padding: 0.92rem;
    border: 1px solid var(--report-border);
    border-radius: var(--report-radius);
    background:
      linear-gradient(180deg, rgba(222, 238, 255, 0.044), rgba(222, 238, 255, 0.018)),
      rgba(13, 25, 41, 0.72);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
  }

  .report-section h3 {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    margin: 0 0 0.85rem;
    color: var(--report-text);
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .report-section h3::before {
    content: '';
    width: 0.22rem;
    height: 0.9rem;
    border-radius: 999px;
    background: var(--report-accent);
    box-shadow: 0 0 12px rgba(95, 215, 232, 0.32);
  }

  .field {
    margin-bottom: 0.82rem;
  }

  .textarea,
  .input,
  .select select {
    min-height: 2.26rem;
    padding-right: 2.35rem;
    border-color: rgba(190, 214, 238, 0.16);
    border-radius: 6px;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.032), rgba(255, 255, 255, 0.012)),
      rgba(12, 22, 37, 0.88);
    color: var(--report-text);
    font-size: 0.82rem;
    font-weight: 600;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.18);
    transition: border-color 0.16s ease, box-shadow 0.16s ease, background-color 0.16s ease;
  }

  .textarea {
    line-height: 1.45;
    resize: vertical;
  }

  .textarea:hover,
  .input:hover,
  .select select:hover {
    border-color: rgba(190, 214, 238, 0.28);
  }

  .textarea:focus,
  .input:focus,
  .select select:focus {
    border-color: var(--report-accent);
    box-shadow: 0 0 0 2px rgba(95, 215, 232, 0.15), inset 0 1px 2px rgba(0, 0, 0, 0.16);
    outline: none;
  }

  .textarea::placeholder,
  .input::placeholder {
    color: var(--report-subtle);
  }

  .input:disabled {
    color: rgba(210, 222, 234, 0.52);
    background: rgba(12, 22, 37, 0.46);
    opacity: 1;
  }

  .select:not(.is-multiple):not(.is-loading)::after {
    border-color: var(--report-muted);
  }

  .label {
    margin-bottom: 0.34rem;
    color: var(--report-muted);
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .help {
    margin-top: 0.28rem;
    color: var(--report-subtle);
    font-size: 0.72rem;
    line-height: 1.3;
  }

  .save-placeholder-message {
    color: $warning;
  }

  .structured-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.72rem;
  }

  .measurement-grid {
    grid-template-columns: repeat(3, minmax(96px, 1fr)) minmax(64px, 0.5fr);
  }

  .weight-grid {
    grid-template-columns: minmax(170px, 1fr) minmax(170px, 1fr);
  }

  .prostate-grid {
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  }

  .input-with-unit {
    display: flex;
    align-items: center;
    gap: 0.46rem;
  }

  .input-with-unit .input {
    min-width: 0;
  }

  .unit-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2.5rem;
    min-height: 2.26rem;
    padding: 0 0.55rem;
    border: 1px solid rgba(190, 214, 238, 0.14);
    border-radius: 6px;
    background: rgba(222, 238, 255, 0.06);
    color: var(--report-muted);
    font-size: 0.76rem;
    font-weight: 700;
  }

  .measurement-unit-field {
    display: flex;
    flex-direction: column;
  }

  .field-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.42rem;
  }

  .field-heading .label {
    margin-bottom: 0;
  }

  .add-measurement-button {
    color: #dffbff;
  }

  .measurement-help {
    margin-bottom: 0.55rem;
  }

  .measurement-summary {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: 0.72rem;
    padding: 0.48rem 0.68rem;
    border: 1px solid rgba(95, 215, 232, 0.2);
    border-radius: 6px;
    background: rgba(95, 215, 232, 0.075);
  }

  .measurement-summary .small-label {
    margin-bottom: 0;
  }

  .measurement-summary strong {
    color: var(--report-text);
    font-size: 0.96rem;
  }

  .measurement-items {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }

  .measurement-item {
    padding: 0.78rem;
    border: 1px solid rgba(190, 214, 238, 0.13);
    border-radius: 7px;
    background: rgba(222, 238, 255, 0.036);
  }

  .measurement-item-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.55rem;
    color: var(--report-muted);
    font-size: 0.76rem;
    font-weight: 700;
  }

  .small-label {
    display: block;
    margin-bottom: 0.3rem;
    color: var(--report-muted);
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .tumour-type-tree {
    display: flex;
    flex-direction: column;
    gap: 0.68rem;
  }

  .tumour-type-group {
    padding: 0.68rem;
    border: 1px solid rgba(190, 214, 238, 0.13);
    border-radius: 7px;
    background: rgba(222, 238, 255, 0.035);
  }

  .tumour-type-group-label {
    margin-bottom: 0.52rem;
    color: var(--report-text);
    font-size: 0.78rem;
    font-weight: 700;
  }

  .tumour-type-children {
    padding-left: 0.4rem;
  }

  .checkbox-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.52rem 0.72rem;
  }

  .prostate-site-grid {
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  }

  .prostate-site-choice {
    display: flex;
    flex-direction: column;
    gap: 0.42rem;
    min-width: 0;
  }

  .prostate-site-choice .report-checkbox {
    margin-bottom: 0;
  }

  .prostate-site-choice strong {
    color: var(--report-accent);
  }

  .prostate-custom-site-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.45rem;
  }

  .prostate-section-rows {
    display: flex;
    flex-direction: column;
    gap: 0.72rem;
  }

  .prostate-section-row {
    padding: 0.72rem;
    border: 1px solid rgba(190, 214, 238, 0.13);
    border-radius: 7px;
    background: rgba(222, 238, 255, 0.032);
  }

  .prostate-row-title,
  .prostate-specimen-title {
    display: flex;
    align-items: center;
    gap: 0.48rem;
    margin-bottom: 0.58rem;
    color: var(--report-text);
    font-size: 0.8rem;
    font-weight: 700;
  }

  .specimen-code {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.45rem;
    height: 1.45rem;
    border: 1px solid rgba(95, 215, 232, 0.24);
    border-radius: 5px;
    background: rgba(95, 215, 232, 0.08);
    color: var(--report-accent);
    font-size: 0.74rem;
    line-height: 1;
  }

  .prostate-macro-grid,
  .prostate-cancer-grid,
  .prostate-clinical-grid,
  .prostate-other-grid {
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  }

  .prostate-cancer-fields {
    margin-top: 0.64rem;
    padding-top: 0.64rem;
    border-top: 1px solid rgba(190, 214, 238, 0.12);
  }

  .report-checkbox {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    min-height: 2rem;
    padding: 0.42rem 0.48rem;
    border: 1px solid rgba(190, 214, 238, 0.1);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.022);
    color: var(--report-text);
    font-size: 0.8rem;
    line-height: 1.3;
    transition: border-color 0.16s ease, background-color 0.16s ease;
  }

  .report-checkbox:hover {
    border-color: rgba(95, 215, 232, 0.24);
    background: rgba(95, 215, 232, 0.06);
    color: var(--report-text);
  }

  .report-checkbox:hover span,
  .report-checkbox:focus-within span {
    color: var(--report-text);
  }

  .report-checkbox input {
    width: 0.95rem;
    height: 0.95rem;
    margin-top: 0.08rem;
    accent-color: var(--report-accent);
  }

  .structured-grid .field {
    margin-bottom: 0;
  }

  .report-preview-heading {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .report-preview-heading h3 {
    margin-bottom: 0;
  }

  .report-preview {
    min-height: 14rem;
    max-height: 26rem;
    overflow: auto;
    padding: 0.88rem;
    border: 1px solid rgba(190, 214, 238, 0.15);
    border-radius: 7px;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.028), rgba(255, 255, 255, 0.01)),
      rgba(5, 12, 22, 0.62);
    color: #eaf6ff;
    font-family: inherit;
    font-size: 0.81rem;
    line-height: 1.58;
    white-space: pre-wrap;
  }

  .report-footer {
    position: sticky;
    bottom: -0.85rem;
    display: flex;
    justify-content: flex-end;
    gap: 0.55rem;
    margin: 0.95rem -0.85rem -0.85rem;
    padding: 0.78rem 0.85rem;
    border-top: 1px solid rgba(190, 214, 238, 0.13);
    background:
      linear-gradient(180deg, rgba(20, 33, 52, 0.9), rgba(15, 26, 42, 0.96));
    backdrop-filter: blur(10px);
  }
}

@media screen and (max-width: 768px) {
  .pathology-report-panel {
    .report-toolbar {
      grid-template-columns: 1fr 1fr;
    }

    .report-disease-field {
      grid-column: 1 / -1;
    }

    .measurement-grid,
    .weight-grid {
      grid-template-columns: 1fr;
    }

    .checkbox-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
